export type LngLat = [number, number]

export type BuildingFeature = {
  id: string
  height: number
  levels: number | null
  ring: LngLat[]
}

const METERS_PER_LEVEL = 3
const DEFAULT_HEIGHT = 9

/** Offset a point by distance meters toward compass bearing (0=N, clockwise). */
export function offsetByBearing(
  lng: number,
  lat: number,
  distanceM: number,
  bearingDeg: number,
): LngLat {
  const R = 6378137
  const br = (bearingDeg * Math.PI) / 180
  const lat1 = (lat * Math.PI) / 180
  const lng1 = (lng * Math.PI) / 180
  const ang = distanceM / R
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(ang) + Math.cos(lat1) * Math.sin(ang) * Math.cos(br),
  )
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(br) * Math.sin(ang) * Math.cos(lat1),
      Math.cos(ang) - Math.sin(lat1) * Math.sin(lat2),
    )
  return [((lng2 * 180) / Math.PI + 540) % 360 - 180, (lat2 * 180) / Math.PI]
}

export function estimateHeight(tags: Record<string, string | undefined>): number {
  const h = tags.height || tags['building:height']
  if (h) {
    const n = parseFloat(String(h).replace(',', '.').replace(/m$/i, ''))
    if (!Number.isNaN(n) && n > 0) return n
  }
  const levels = tags['building:levels'] || tags.levels
  if (levels) {
    const n = parseFloat(String(levels).replace(',', '.'))
    if (!Number.isNaN(n) && n > 0) return n * METERS_PER_LEVEL
  }
  return DEFAULT_HEIGHT
}

/**
 * Project building footprint into a ground shadow polygon.
 * Shadow direction = opposite to sun azimuth (sun azimuth = direction TO sun).
 */
export function buildingShadowPolygon(
  ring: LngLat[],
  heightM: number,
  sunAzimuthDeg: number,
  sunAltitudeDeg: number,
): LngLat[] | null {
  if (sunAltitudeDeg <= 1 || heightM <= 0 || ring.length < 3) return null
  const elev = (sunAltitudeDeg * Math.PI) / 180
  const length = heightM / Math.tan(elev)
  // Cap extreme dawn/dusk shadows
  const capped = Math.min(length, 800)
  const shadowBearing = (sunAzimuthDeg + 180) % 360

  const tips = ring.map(([lng, lat]) => offsetByBearing(lng, lat, capped, shadowBearing))
  // Closed polygon: footprint then reverse tips
  const poly = [...ring, ...tips.slice().reverse()]
  if (
    poly[0][0] !== poly[poly.length - 1][0] ||
    poly[0][1] !== poly[poly.length - 1][1]
  ) {
    poly.push(poly[0])
  }
  return poly
}

export function shadowsGeoJSON(
  buildings: BuildingFeature[],
  sunAzimuthDeg: number,
  sunAltitudeDeg: number,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []
  for (const b of buildings) {
    const poly = buildingShadowPolygon(b.ring, b.height, sunAzimuthDeg, sunAltitudeDeg)
    if (!poly) continue
    features.push({
      type: 'Feature',
      properties: { buildingId: b.id, height: b.height },
      geometry: { type: 'Polygon', coordinates: [poly] },
    })
  }
  return { type: 'FeatureCollection', features }
}

/** Ray from point toward the sun; true if blocked by a building volume (2.5D). */
export function isPointIlluminated(
  lng: number,
  lat: number,
  buildings: BuildingFeature[],
  sunAzimuthDeg: number,
  sunAltitudeDeg: number,
): boolean {
  if (sunAltitudeDeg <= 0) return false

  const elev = (sunAltitudeDeg * Math.PI) / 180
  const maxDist = 600
  const steps = 40
  const step = maxDist / steps

  for (let i = 1; i <= steps; i++) {
    const dist = i * step
    const [xl, yl] = offsetByBearing(lng, lat, dist, sunAzimuthDeg)
    const rayHeight = dist * Math.tan(elev)

    for (const b of buildings) {
      if (!pointInRing(xl, yl, b.ring)) continue
      if (rayHeight < b.height) return false
    }
  }
  return true
}

function pointInRing(lng: number, lat: number, ring: LngLat[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const intersect =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi + 0.0) + xi
    if (intersect) inside = !inside
  }
  return inside
}
